/**
 * Per-section diff history modal (#936 D6).
 *
 * Loads `Strategy Section Diffs` for one StrategySection and renders a vertical
 * timeline of changes (newest first). Each entry shows: timestamp, change
 * type, who made the change (user or agent), rationale, and a side-by-side
 * Previous → New body diff.
 *
 * No Kendo dependency — Marketing-UI does not include @progress/kendo at this
 * package layer. We use a plain styled overlay consistent with other workspace
 * sections (`mkt-ws-` selectors).
 *
 * "Reset to this version" is gated by the `canEdit` input. The actual reset
 * mutation is a follow-up: this card's button currently logs the request and
 * closes the modal.
 */
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, ChangeDetectorRef } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import { marketingStrategySectionDiffEntity } from 'mj_generatedentities';

interface DiffRow {
    id: string;
    changeType: string;
    changedAt: Date;
    changedByLabel: string;
    rationale: string | null;
    previousBody: string | null;
    newBody: string | null;
    previousConfidence: string | null;
    newConfidence: string | null;
}

@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-section-diff-modal',
    template: `
<div class="dm__overlay" *ngIf="open" (click)="onBackdropClick($event)">
    <div class="dm__panel" (click)="$event.stopPropagation()">
        <div class="dm__head">
            <div>
                <div class="dm__caps">Section history</div>
                <div class="dm__title">{{ sectionTitle || 'Strategy section diffs' }}</div>
            </div>
            <button type="button" class="dm__close" (click)="close.emit()" aria-label="Close">
                <i class="fa-solid fa-xmark"></i>
            </button>
        </div>

        <div class="dm__body">
            <div *ngIf="loading" class="dm__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading…</div>
            <div *ngIf="!loading && diffs.length === 0" class="dm__empty">No history yet for this section.</div>

            <div *ngFor="let d of diffs" class="dm__row">
                <div class="dm__row-head">
                    <span class="dm__type" [attr.data-type]="d.changeType">{{ d.changeType }}</span>
                    <span class="dm__when">{{ formatDate(d.changedAt) }}</span>
                    <span class="dm__who">{{ d.changedByLabel }}</span>
                    <button *ngIf="canEdit && d.previousBody !== null" type="button" class="dm__reset" (click)="onReset(d)">
                        <i class="fa-solid fa-rotate-left"></i> Reset to this version
                    </button>
                </div>

                <div *ngIf="d.rationale" class="dm__rationale">"{{ d.rationale }}"</div>

                <div class="dm__diff">
                    <div class="dm__col">
                        <div class="dm__col-head">
                            <span class="dm__caps">Before</span>
                            <span *ngIf="d.previousConfidence" class="dm__pill dm__pill--{{ pillClass(d.previousConfidence) }}">{{ d.previousConfidence }}</span>
                        </div>
                        <pre class="dm__pre">{{ d.previousBody || '(none)' }}</pre>
                    </div>
                    <div class="dm__col">
                        <div class="dm__col-head">
                            <span class="dm__caps">After</span>
                            <span *ngIf="d.newConfidence" class="dm__pill dm__pill--{{ pillClass(d.newConfidence) }}">{{ d.newConfidence }}</span>
                        </div>
                        <pre class="dm__pre">{{ d.newBody || '(none)' }}</pre>
                    </div>
                </div>
            </div>
        </div>

        <div class="dm__foot">
            <button type="button" class="dm__btn" (click)="close.emit()">Close</button>
        </div>
    </div>
</div>
    `,
    styles: [`
        :host { display: contents; }
        .dm__overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .dm__panel { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 12px; max-width: 1080px; width: 92vw; max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.18); }
        .dm__head { display: flex; align-items: flex-start; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .dm__caps { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-bottom: 4px; }
        .dm__title { font-size: 17px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); }
        .dm__close { background: none; border: none; cursor: pointer; font-size: 16px; padding: 4px 8px; color: var(--mj-text-secondary, #71717a); }
        .dm__close:hover { color: var(--mj-text-primary, #1a1a1a); }

        .dm__body { padding: 16px 20px; overflow-y: auto; flex: 1; }
        .dm__loading, .dm__empty { padding: 40px; text-align: center; color: var(--mj-text-secondary, #71717a); font-size: 13px; }

        .dm__row { padding: 16px 0; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .dm__row:last-child { border-bottom: none; }
        .dm__row-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 8px; }
        .dm__type { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 4px; background: var(--mj-bg-page, #f5f5f5); color: var(--mj-text-primary, #1a1a1a); }
        .dm__type[data-type="Edit"] { background: #eff6ff; color: #1d4ed8; }
        .dm__type[data-type="AutoApply"] { background: #ecfdf5; color: #047857; }
        .dm__type[data-type="Approve"] { background: #f0fdf4; color: #166534; }
        .dm__type[data-type="Reject"] { background: #fef2f2; color: #b91c1c; }
        .dm__type[data-type="Reset"] { background: #fffbeb; color: #b45309; }
        .dm__when { font-size: 12px; color: var(--mj-text-secondary, #71717a); font-family: ui-monospace, monospace; }
        .dm__who { font-size: 12px; color: var(--mj-text-primary, #1a1a1a); }
        .dm__reset { margin-left: auto; background: none; border: 1px solid var(--mj-border-default, #e0e0e0); padding: 4px 10px; border-radius: 6px; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); }
        .dm__reset:hover { background: var(--mj-bg-page, #f5f5f5); }

        .dm__rationale { font-size: 12.5px; color: var(--mj-text-secondary, #4b5563); font-style: italic; margin: 4px 0 12px; }

        .dm__diff { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .dm__col { background: var(--mj-bg-page, #f5f5f5); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; padding: 10px 12px; }
        .dm__col-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
        .dm__pre { white-space: pre-wrap; word-break: break-word; font-family: ui-monospace, monospace; font-size: 12px; color: var(--mj-text-primary, #1a1a1a); margin: 0; max-height: 280px; overflow-y: auto; }

        .dm__pill { font-size: 10.5px; padding: 1px 7px; border-radius: 999px; font-weight: 500; }
        .dm__pill--good { background: #dcfce7; color: #166534; }
        .dm__pill--warn { background: #fef9c3; color: #854d0e; }
        .dm__pill--bad { background: #fee2e2; color: #b91c1c; }
        .dm__pill--muted { background: #e5e7eb; color: #374151; }

        .dm__foot { display: flex; justify-content: flex-end; padding: 12px 20px; border-top: 1px solid var(--mj-border-default, #e0e0e0); }
        .dm__btn { padding: 6px 14px; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); cursor: pointer; font-size: 13px; }

        @media (max-width: 720px) {
            .dm__diff { grid-template-columns: 1fr; }
        }
    `]
})
export class StrategySectionDiffModalComponent implements OnChanges {
    @Input() open = false;
    @Input() sectionId: string | null = null;
    @Input() sectionTitle = '';
    @Input() canEdit = false;

    @Output() close = new EventEmitter<void>();
    @Output() resetRequested = new EventEmitter<{ diffId: string; previousBody: string | null }>();

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    diffs: DiffRow[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['open'] && this.open && this.sectionId)
            || (changes['sectionId'] && this.open && this.sectionId)) {
            void this.load();
        }
    }

    onBackdropClick(_e: Event): void {
        this.close.emit();
    }

    onReset(d: DiffRow): void {
        // Stub per spec — the actual reset flow is a follow-up.
        // We emit so a parent can wire it up later, then close the modal.
        // eslint-disable-next-line no-console
        console.log('[StrategySectionDiffModal] reset requested for diff', d.id);
        this.resetRequested.emit({ diffId: d.id, previousBody: d.previousBody });
        this.close.emit();
    }

    formatDate(d: Date): string {
        return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    }

    pillClass(c: string | null): string {
        switch (c) {
            case 'Strong': return 'good';
            case 'Moderate': return 'warn';
            case 'Weak': return 'bad';
            default: return 'muted';
        }
    }

    private async load(): Promise<void> {
        if (!this.sectionId) return;
        this.loading = true;
        this.diffs = [];
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const result = await rv.RunView<marketingStrategySectionDiffEntity>({
                EntityName: 'Strategy Section Diffs',
                ExtraFilter: `StrategySectionID='${this.sectionId}'`,
                OrderBy: 'ChangedAt DESC',
                MaxRows: 100,
                ResultType: 'simple',
            });
            if (!result.Success) {
                LogError(`[StrategySectionDiffModal] query failed: ${result.ErrorMessage ?? 'unknown'}`);
                return;
            }
            const rows = (result.Results ?? []) as marketingStrategySectionDiffEntity[];
            this.diffs = rows.map(r => ({
                id: r.ID,
                changeType: r.ChangeType,
                changedAt: r.ChangedAt,
                changedByLabel: r.ChangedByUser ?? r.ChangedByAgentRun ?? 'System',
                rationale: r.ChangeRationale,
                previousBody: r.PreviousBody,
                newBody: r.NewBody,
                previousConfidence: r.PreviousConfidenceLevel,
                newConfidence: r.NewConfidenceLevel,
            }));
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategySectionDiffModal] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }
}
